import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sensitivePermissions, type Permission } from '@holymoly/core';
import { PageTitle } from '@/components/PageTitle';
import { Field } from '@/components/ui/Field';
import { ConfirmDialog, Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toaster';
import { errorText } from '@/lib/errors';
import { requirePermission } from '@/lib/guards';
import { client, orpc } from '@/lib/orpc';
import { useCan } from '@/lib/session';

export const Route = createFileRoute('/_app/admin/roles')({
  beforeLoad: ({ context }) => requirePermission(context.me, 'users.view', 'roles.manage'),
  component: RolesPage
});

type Role = Awaited<ReturnType<typeof client.roles.list>>[number];
type Draft = { id?: string; name: string; description: string; grants: Permission[]; requireTwoFactor: boolean };

function RolesPage() {
  const { t } = useTranslation();
  const can = useCan();
  const toast = useToast();
  const queryClient = useQueryClient();
  const roles = useQuery(orpc.roles.list.queryOptions());
  const manage = can('roles.manage');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [deleting, setDeleting] = useState<Role | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: orpc.roles.key() });
  const remove = useMutation({
    mutationFn: (id: string) => client.roles.delete({ id }),
    onSuccess: async () => {
      setDeleting(null);
      await refresh();
    },
    onError: (e) => {
      setDeleting(null);
      toast('error', errorText(t, e));
    }
  });

  return (
    <div className="space-y-6 p-6">
      <PageTitle
        title={t('admin.roles')}
        items={[{ label: t('admin.section') }, { label: t('admin.roles'), active: true }]}
        actions={
          manage && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setDraft({ name: '', description: '', grants: [], requireTwoFactor: false })}>
              <span className="iconify lucide--plus size-4" />
              {t('admin.newRole')}
            </button>
          )
        }
      />
      <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
        {roles.data?.map((r) => (
          <div key={r.id} className="card bg-base-100 shadow-sm">
            <div className="card-body">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="flex items-center gap-2 font-semibold">
                    {r.name}
                    {r.system && <span className="badge badge-neutral badge-sm">{t('admin.system')}</span>}
                    {r.requireTwoFactor && <span className="iconify lucide--shield-check text-success size-4" title={t('admin.requireTwoFactor')} />}
                  </h2>
                  <p className="text-base-content/60 mt-1 text-sm">{r.description}</p>
                </div>
                <span className="text-base-content/60 shrink-0 text-xs">{t('admin.members', { count: r.members })}</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {r.grants.includes('*') ? (
                  <span className="badge badge-soft badge-primary badge-sm">{t('admin.allPermissions')}</span>
                ) : (
                  r.grants.map((g) => (
                    <span key={g} className="badge badge-ghost badge-sm" title={g}>
                      {t(`permissions.${g as Permission}`)}
                    </span>
                  ))
                )}
              </div>
              {manage && !r.system && (
                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline border-base-300"
                    onClick={() =>
                      setDraft({ id: r.id, name: r.name, description: r.description, grants: r.grants as Permission[], requireTwoFactor: r.requireTwoFactor })
                    }>
                    {t('admin.editRole')}
                  </button>
                  <button type="button" className="btn btn-sm btn-ghost text-error" onClick={() => setDeleting(r)} disabled={r.members > 0}>
                    {t('admin.deleteRole')}
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {draft && (
        <RoleEditor
          draft={draft}
          onClose={() => setDraft(null)}
          onSaved={async () => {
            setDraft(null);
            await refresh();
          }}
        />
      )}
      <ConfirmDialog
        open={deleting !== null}
        danger
        busy={remove.isPending}
        text={deleting && t('admin.confirmDeleteRole', { name: deleting.name })}
        onClose={() => setDeleting(null)}
        onConfirm={() => remove.mutate(deleting!.id)}
      />
    </div>
  );
}

function RoleEditor({ draft, onClose, onSaved }: { draft: Draft; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const catalog = useQuery(orpc.roles.permissions.queryOptions());
  const [d, setD] = useState(draft);
  const forced = useMemo(() => d.grants.some((g) => sensitivePermissions.includes(g)), [d.grants]);
  const toggle = (p: Permission, on: boolean) => setD({ ...d, grants: on ? [...d.grants, p] : d.grants.filter((x) => x !== p) });

  const save = useMutation({
    mutationFn: () => {
      const body = { name: d.name, description: d.description, grants: d.grants, requireTwoFactor: d.requireTwoFactor || forced };
      return d.id ? client.roles.update({ id: d.id, ...body }) : client.roles.create(body);
    },
    onSuccess: onSaved,
    onError: (e) => toast('error', errorText(t, e))
  });

  return (
    <Modal
      open
      size="lg"
      onClose={onClose}
      title={d.id ? t('admin.editRole') : t('admin.newRole')}
      actions={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t('admin.cancel')}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => save.mutate()} disabled={save.isPending || !d.name.trim()}>
            {t('account.save')}
          </button>
        </>
      }>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('admin.roleName')}>
          {(id) => <input id={id} className="input w-full" value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} maxLength={60} autoFocus />}
        </Field>
        <Field label={t('admin.roleDescription')}>
          {(id) => <input id={id} className="input w-full" value={d.description} onChange={(e) => setD({ ...d, description: e.target.value })} maxLength={300} />}
        </Field>
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        {catalog.data?.map((group) => (
          <fieldset key={group.module} className="border-base-300 rounded-box border p-3">
            <legend className="px-1 text-sm font-medium">{t(`permissionModules.${group.module}` as never)}</legend>
            {group.permissions.map(({ key, sensitive }) => (
              <label key={key} className="flex items-start gap-2 py-1 text-sm">
                <input type="checkbox" className="checkbox checkbox-sm mt-0.5" checked={d.grants.includes(key)} onChange={(e) => toggle(key, e.target.checked)} />
                <span>
                  {t(`permissions.${key}`)}
                  {sensitive && <span className="badge badge-warning badge-soft badge-xs ms-1">{t('admin.sensitive')}</span>}
                </span>
              </label>
            ))}
          </fieldset>
        ))}
      </div>
      <label className="mt-4 flex items-center gap-2">
        <input
          type="checkbox"
          className="toggle toggle-sm"
          checked={d.requireTwoFactor || forced}
          disabled={forced}
          onChange={(e) => setD({ ...d, requireTwoFactor: e.target.checked })}
        />
        <span className="text-sm">{t('admin.requireTwoFactor')}</span>
      </label>
      {forced && <p className="text-base-content/60 mt-1 text-xs">{t('admin.requireTwoFactorForced')}</p>}
    </Modal>
  );
}
